import { Button, Col, Row } from 'antd';
import { map, max, some } from 'lodash-es';
import classNames from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useResponsiveQueries } from '../../../components/responsive/responsiveQueries';
import { PackageListDimensionsInputs } from './PackageListDimensionsInputs';
import { SymbolCol } from './packageListCommon';
import {
  DimensionalWeightResult,
  WeightInput,
} from './PackageListPackageEditingMode';
import { FAIcon } from '../../../components/adapters/fontAwesomeAdapters';
import { useFormContext } from '../../../components/forms/forms';
import {
  extractActualWeight,
  extractBilledWeight,
  getPackageTotal,
} from '../shipmentCommon';
import { FormItemFieldRegistration } from '../../../components/forms/basicFormElements';
import { DynamicFormSchemaContext } from '../../../components/forms/dynamic/dynamicFormSchema';
import {
  UnitSystems,
  WeightUnit,
  convertUnit,
} from '../../../app/enums/measuringUnits';
import { SettingsContext } from '../../../components/domainSpecific/settingsElements';
import { useQuoteMessage } from '../../../components/dialogs/QuoteRestrictionsMessage';
import { FormItemSchemaDefinedSelect } from '../../../components/forms/selects';
import { useQuotePackagesEditingModeStyles } from './QuotePackagesEditingMode.styles';
import { usePackageListCommonStyles } from './packageListCommon.styles';

function QuoteWeightDimsRow({
  index,
  setIsLoading,
  skipDimensionalWeightComputation,
}) {
  const { styles } = useQuotePackagesEditingModeStyles();
  const media = useResponsiveQueries();
  const symbolWidth = media.xs ? 25 : 32;
  const isSm = media.xs || media.sm;
  const { formInstance, parentContext, values } = useFormContext();
  const { schema } = useContext(DynamicFormSchemaContext);
  const { unitSystem } = useContext(SettingsContext);

  useEffect(() => {
    const allPackagesArray = map(
      parentContext?.values?.packages || [],
      (p, idx) => {
        if (idx === index) return values;
        return p;
      }
    );
    const totalWeightActual = getPackageTotal(
      allPackagesArray,
      extractActualWeight
    );
    const totalWeightBilled = getPackageTotal(
      allPackagesArray,
      extractBilledWeight
    );

    const totalLbs =
      convertUnit(
        max([totalWeightActual, totalWeightBilled]),
        UnitSystems[unitSystem].weight,
        WeightUnit.POUND
      ) || 0;
    formInstance.setFieldsValue({
      maxWeightValidator: totalLbs,
    });
    formInstance.validateFields(['maxWeightValidator']);
  }, [parentContext, formInstance, index, values, unitSystem]);

  return (
    <>
      <FormItemFieldRegistration
        name="maxWeightValidator"
        rules={schema?.packages?.maxWeightValidator?.validation?.rules}
      />
      <Row className={styles.quotePackageWeightResult}>
        <Col flex="8 1 0%" style={{ display: 'flex', shrink: 0 }}>
          <DimensionalWeightResult
            index={index}
            separator="="
            labelId="book.newShipment.package.dims.dimensionalWeight"
            onLoadingChange={setIsLoading}
            skipDimensionalWeightComputation={skipDimensionalWeightComputation}
          />
        </Col>
        {isSm && (
          <>
            <Col flex="3">
              <WeightInput index={index} hideSuffix />
            </Col>
          </>
        )}
      </Row>
      <Row
        align="top"
        className={classNames(
          'quote-packages-dim-inputs',
          styles.quotePackagesInputs
        )}
      >
        {!isSm && (
          <>
            <Col flex="3">
              <WeightInput index={index} className={styles.quoteWeightInput} />
            </Col>
            <SymbolCol className="DimensionSeparator">
              <FAIcon icon="plus-square" className="icon-22" />
            </SymbolCol>
          </>
        )}
        <PackageListDimensionsInputs
          index={index}
          inputColProps={{ flex: '3' }}
          symbolColProps={{ style: { width: symbolWidth } }}
          inputProps={{
            isShipment: false,
            hideSuffix: isSm,
            alwaysEnabled: true,
          }}
        />
      </Row>
    </>
  );
}

export function QuotePackagesEditingMode({
  index,
  saveChanges,
  hasError,
  remove,
  skipDimensionalWeightComputation,
  showWeightErrorMessage,
}) {
  const { styles } = useQuotePackagesEditingModeStyles();
  const { styles: commonStyles } = usePackageListCommonStyles();
  const [packagingTypeDisabled, setPackagingTypeDisabled] = useState(true);
  const media = useResponsiveQueries();
  const isXs = media.xs;

  const { formatMessage } = useIntl();
  const { showQuoteMessage } = useQuoteMessage();
  const { formInstance } = useFormContext();
  const errorVisible = useRef();
  const [isLoading, setIsLoading] = useState(false);

  const fieldsErrors = formInstance.getFieldsError();
  const anyErrors = some(fieldsErrors, f => some(f.errors));

  const maxWeightError = formInstance.getFieldError('maxWeightValidator');
  useEffect(() => {
    if (
      showWeightErrorMessage &&
      !errorVisible.current &&
      some(maxWeightError)
    ) {
      errorVisible.current = true;
      showQuoteMessage(formatMessage, () => {
        errorVisible.current = false;
      });
    }
  }, [maxWeightError, formatMessage, showWeightErrorMessage, showQuoteMessage]);

  return (
    <div
      className={classNames(
        'NewShipmentPackageListForm',
        styles.newShipmentPackageListForm,
        {
          [styles.hasError]: hasError,
        }
      )}
    >
      <div
        className={classNames(
          'new-quote-package-header',
          styles.newQuotePackageHeader
        )}
      >
        <p>
          <FormattedMessage
            id="book.newQuote.packageItemHeaderWithNumber"
            values={{ index: index + 1 }}
          />
        </p>
        <div className={styles.quotePackageButton}>
          <Button
            type="primary"
            size="small"
            onClick={saveChanges}
            data-subject="packages"
            data-action="save"
            className={styles.quotesCalculateButton}
            htmlType="submit"
            disabled={anyErrors || isLoading}
          >
            <FormattedMessage id="book.newShipment.packages.save-short" />
          </Button>

          {index > 0 && (
            <FAIcon onClick={remove} icon="times-circle" className="icon-15" />
          )}
        </div>
      </div>
      <Row className={styles.quotePackagesInputs} gutter={20}>
        <Col span={12}>
          <FormItemSchemaDefinedSelect
            preloadOptions
            name="packageSource"
            schemaName="packages.packageSource"
            disabled
            labelId={
              isXs
                ? 'book.newShipment.label.packageSource-xs'
                : 'book.newShipment.label.packageSource'
            }
          />
        </Col>
        <Col span={12}>
          <FormItemSchemaDefinedSelect
            disabled={packagingTypeDisabled}
            onAutofillSkip={() => setPackagingTypeDisabled(false)}
            preloadOptions
            name="packagingType"
            labelId={
              isXs
                ? 'book.newShipment.label.packagingType-xs'
                : 'book.newShipment.label.packagingType'
            }
            schemaName="packages.packagingType"
          />
        </Col>
      </Row>
      <div>
        <div className="spaces-vert-md">
          <QuoteWeightDimsRow
            index={index}
            setIsLoading={setIsLoading}
            skipDimensionalWeightComputation={skipDimensionalWeightComputation}
          />
        </div>

        <div
          style={{ marginLeft: 20 }}
          className={classNames(
            'NewShipmentPackage-ErrorMessage',
            commonStyles.newShipmentPackageErrorMessage,
            {
              'has-content': some(maxWeightError),
            }
          )}
        >
          {some(maxWeightError) && (
            <FormattedMessage id="book.newQuote.over-max-weight" />
          )}

          {hasError && !some(maxWeightError) && (
            <FormattedMessage id="book.newQuote.error.package" />
          )}
        </div>
      </div>
    </div>
  );
}
